import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { ImageAssetWithAltText } from '@fhs/ui';
import { useFlag } from '@fhs-legacy/frontend/src/utils/launchdarkly';

import { PICKER_SELECTION_TYPE } from '../components/guide/constants';
import type { PickerSelectionType, Step } from '../components/guide/types';
import { useMenuGuideQuery } from '../queries/use-menu-guide-query';
import { useMenuQueryOpts } from '../queries/use-menu-query-opts';
import type {
  CustomizationSelections,
  EditComboItem,
  MenuPickerData,
  SelectedCustomization,
} from '../types';
import { buildDefaultComboSelectedPickers } from '../utils/combo-selection-readiness';

import { useMenuPicker } from './menu-picker';

export type SelectedPicker = {
  id: string;
  slug: string;
  // Derived
  displayName: string;
  priceCents: number;
  basePriceCents?: number;
  baseCalories?: number;

  // Instructions
  instructions?: string;

  // Picker
  pickerSelections?: Record<string, string | null>;

  // PickerAspectOption
  selectedPickerAspectOptionId?: string;
  selectedPickerAspectOption?:
    | null
    | NonNullable<MenuPickerData['pickerAspect']>['options'][number];

  // Customizations
  defaultCustomizationSelections?: CustomizationSelections;
  userCustomizationSelections?: CustomizationSelections;
  resolvedCustomizationSelections?: CustomizationSelections;

  draftCustomizationSelections?: CustomizationSelections;
  requiresCustomization: boolean;

  // Formatted customization options to render on "Make it a combo" section
  selectedCustomizationOptions?: SelectedCustomization[];

  // UI
  image?: ImageAssetWithAltText;
};

export type ComboSelectionContext = {
  isComboEnabled: boolean;
  isComboAvailable: boolean;
  selectedPickers: Record<string, SelectedPicker>;
  isEditingPickers: boolean;
  setIsComboEnabled: (enabled: boolean) => void;
  setSelectedPickers: (pickers: Record<string, SelectedPicker>) => void;
  addOrUpdateSelectedPicker: (step: string, picker: SelectedPicker) => void;
  setIsEditingPickers: (editing: boolean) => void;
  isComboGuideOpen: boolean;
  setIsComboGuideOpen: (value: boolean) => void;
  shouldAddToCartDirectly: boolean;
  setShouldAddToCartDirectly: (value: boolean) => void;
  comboModalDisplayed: boolean;
  setComboModalDisplayed: (value: boolean) => void;
  guideSteps: Array<Step>;
  isGuideLoading: boolean;
  pickerSelectionType: PickerSelectionType;
  setPickerSelectionType: (value: PickerSelectionType) => void;
};

type ComboSelectionProviderStateProps = {
  children?: ReactNode;
  guideSteps: Array<Step>;
  isGuideLoading: boolean;
  editComboItems?: EditComboItem[];
  isEditComboItemsLoading?: boolean;
  initialState?: {
    isComboEnabled?: boolean;
    isComboAvailable?: boolean;
    comboSelectedPickers?: Record<string, SelectedPicker>;
  };
};

const comboSelectionContext = createContext<ComboSelectionContext | null>(null);

const ComboSelectionProviderState = ({
  children,
  initialState,
  guideSteps,
  isGuideLoading,
  editComboItems,
  isEditComboItemsLoading,
}: ComboSelectionProviderStateProps) => {
  const enableComboDefaultSelection = useFlag('enable-menu-details-default-combo-selection');

  const getInitialPickerSelectionType = () => {
    if (initialState && initialState.isComboEnabled !== undefined) {
      return initialState.isComboEnabled
        ? PICKER_SELECTION_TYPE.COMBO
        : PICKER_SELECTION_TYPE.JUST_SUB;
    }

    return enableComboDefaultSelection
      ? PICKER_SELECTION_TYPE.COMBO
      : PICKER_SELECTION_TYPE.JUST_SUB;
  };

  const [pickerSelectionType, setPickerSelectionType] = useState(getInitialPickerSelectionType);

  const [selectedPickers, setSelectedPickers] = useState<Record<string, SelectedPicker>>(() => {
    const existingSelectedPickers = initialState?.comboSelectedPickers;
    if (existingSelectedPickers && Object.keys(existingSelectedPickers).length > 0) {
      return existingSelectedPickers;
    }

    if (guideSteps.length > 0 && !isEditComboItemsLoading) {
      return buildDefaultComboSelectedPickers(guideSteps, editComboItems);
    }

    return {};
  });

  useEffect(() => {
    if (!guideSteps.length || isEditComboItemsLoading) {
      return;
    }

    setSelectedPickers(prev => {
      const defaults = buildDefaultComboSelectedPickers(guideSteps, editComboItems);
      const hasMissingDefaults = Object.keys(defaults).some(slotId => !prev[slotId]?.id);

      if (!hasMissingDefaults && Object.keys(prev).length > 0) {
        return prev;
      }

      if (Object.keys(defaults).length === 0 && Object.keys(prev).length === 0) {
        return prev;
      }

      // User selections win over defaults; fill in any slots that are still empty.
      return { ...defaults, ...prev };
    });
  }, [guideSteps, editComboItems, isEditComboItemsLoading]);
  const [isEditingPickers, setIsEditingPickers] = useState(false);
  const [isComboGuideOpen, setIsComboGuideOpen] = useState(false);
  const [shouldAddToCartDirectly, setShouldAddToCartDirectly] = useState(false);

  // Used to not display "Make it a Combo" modal more than once if user already opted for just the sub
  const [comboModalDisplayed, setComboModalDisplayed] = useState(false);

  const isComboEnabled = pickerSelectionType === PICKER_SELECTION_TYPE.COMBO;
  const isComboAvailable = initialState?.isComboAvailable ?? true;

  const addOrUpdateSelectedPicker = useCallback(
    (step: string, picker: SelectedPicker) => {
      setSelectedPickers(prevPickers => ({
        ...prevPickers,
        [step]: picker, // Add or replace the picker for the given step
      }));
    },
    [setSelectedPickers]
  );

  const value: ComboSelectionContext = {
    selectedPickers,
    setSelectedPickers,
    addOrUpdateSelectedPicker,
    isEditingPickers,
    setIsEditingPickers,
    isComboEnabled,
    isComboAvailable,
    setIsComboEnabled: () => {}, // TODO: remove/handle this on FHS-4323
    isComboGuideOpen,
    setIsComboGuideOpen,
    shouldAddToCartDirectly,
    setShouldAddToCartDirectly,
    comboModalDisplayed,
    setComboModalDisplayed,
    guideSteps,
    isGuideLoading,
    pickerSelectionType,
    setPickerSelectionType,
  };

  return <comboSelectionContext.Provider value={value}>{children}</comboSelectionContext.Provider>;
};

type ComboSelectionProviderProps = {
  children: ReactNode;
  initialState?: {
    isComboEnabled?: boolean;
    comboSelectedPickers?: Record<string, SelectedPicker>;
  };
  // Combo items belonging to the cart entry currently being edited (edit-from-cart flow).
  // Pass `undefined` when not editing, or while the cart entry's combo items are still loading.
  editComboItems?: EditComboItem[];
  isEditComboItemsLoading?: boolean;
};
export const ComboSelectionProvider = ({
  initialState,
  children,
  editComboItems,
  isEditComboItemsLoading,
}: ComboSelectionProviderProps) => {
  const { query: menuPickerQuery } = useMenuPicker();
  const enableComboLoadingSkeletons = useFlag('enable-simply-menu-details-combo-loading-skeletons');
  const comboId = menuPickerQuery.data?.combo?.id ?? '';

  const menuGuideQuery = useMenuGuideQuery({
    enabled: Boolean(comboId),
    comboId,
    ...useMenuQueryOpts(),
  });

  const isComboRequired = menuPickerQuery.data?.combo?.isRequired;
  const isGuideLoading =
    enableComboLoadingSkeletons &&
    menuGuideQuery.isFetching &&
    !menuGuideQuery.isFetchedAfterMount;

  /**
   * If the menu guide is not available, it might be due to issues like missing or misconfigured combo options.
   * In this case, we assume combos should not be enabled to prevent broken configurations from affecting the UX.
   */
  const isComboMenuGuideAvailable = menuGuideQuery.data?.isAvailable;

  // We want to force combo enabled in case it's required (e.g. Kids Combos)
  const currentComboState =
    isComboMenuGuideAvailable && (isComboRequired ? true : initialState?.isComboEnabled);

  const mergedInitialState = {
    ...initialState,
    // We want to force combo enabled in case it's required (e.g. Kids Combos)
    isComboEnabled: currentComboState,
    // Disable combos if the menu guide is not available, otherwise preserve or enforce the current state.
    isComboAvailable: isComboMenuGuideAvailable,
  };

  // Remount when combo changes OR when the comgo guide data updates.
  const providerKey = [
    comboId,
    initialState && JSON.stringify(mergedInitialState),
    String(isComboMenuGuideAvailable),
  ]
    .filter(Boolean)
    .join('-');

  return (
    <ComboSelectionProviderState
      initialState={mergedInitialState}
      guideSteps={menuGuideQuery.data?.steps ?? []}
      isGuideLoading={isGuideLoading}
      editComboItems={editComboItems}
      isEditComboItemsLoading={isEditComboItemsLoading}
      // Reset combo selection provider state when combo id changes or initialState updates
      key={providerKey}
    >
      {children}
    </ComboSelectionProviderState>
  );
};

export const useComboSelectionContext = (): ComboSelectionContext => {
  const context = useContext(comboSelectionContext);

  if (!context) {
    throw new Error(
      'useComboSelectionContext can only be called from within a descendant of ComboSelectionProvider'
    );
  }

  return context;
};
